/// <reference types="angular-ui-notification" />
import { IRuntimeBridgeClient } from './IRuntimeBridgeClient';
import {
    UploadResponseMessage,
    FrontendReadyMessage,
    ChangeKeyboardLanguageMessage,
    ListKeyboardLanguagesAvailableMessage,
    SetRuntimeLanguageMessage,
    RuntimeBridgeWorkerMessageType,
    UploadProgressMessage,
    UploadFailedMessage,
    UploadSuccessMessage,
    QuitMessage
} from '../../../../serviceworker/src/bridges/runtimeBridgeWorkerMessages';

import SignalsService from '../services/core/SignalsService';
import { IScreenRecordingBridgeClient } from './IScreenRecordingBridgeClient';
import { clearSessionResumeStorage } from '../utils/sessionResumeStorage';

import 'angular';
import 'angular-ui-notification';
type UINotification = angular.uiNotification.INotificationService;

const swc = ((navigator as Navigator).serviceWorker as ServiceWorkerContainer);

/**
 * Progressive Web App implementation of the Runtime Bridge Client.
 * Communicates with the Runtime Bridge Worker via postMessage.
 */
export default class PWARuntimeBridgeClient implements IRuntimeBridgeClient {
    
    
    private signalsService: SignalsService;
    private notify: UINotification;
    private $timeout: angular.ITimeoutService;
    private screenRecordingBridgeClient: IScreenRecordingBridgeClient;

    constructor(signalsService: SignalsService, notify: UINotification, $timeout: angular.ITimeoutService, screenRecordingBridgeClient: IScreenRecordingBridgeClient) {
        this.signalsService = signalsService;
        this.notify = notify;
        this.$timeout = $timeout;
        this.screenRecordingBridgeClient = screenRecordingBridgeClient;
        this.initHandlers();
    }

    /**
     * Initializes message handlers for worker responses
     * Listens for:
     * - Screen recording status
     * - Upload progress/status
     */
    private initHandlers() {
        swc.addEventListener('message', (event) => {
            if (!Object.values(RuntimeBridgeWorkerMessageType).includes(event.data?.type)) {
                return; // Not our message
            }

            if (!event.data || !event.data.type) {
                return;
            }

            if (!Object.values(RuntimeBridgeWorkerMessageType).includes(event.data.type)) {
                return; // Not our message
            }

            console.log("PWARuntimeBridgeClient received message from service worker", event);

            const message = event.data;

            switch (message.type) {
                case RuntimeBridgeWorkerMessageType.screenRecordingStopped:
                    this.signalsService.emit('screenRecordingStopped');
                    break;
                    
                case RuntimeBridgeWorkerMessageType.uploadProgress:
                    const mp = message as UploadProgressMessage;
                    this.signalsService.emit('uploadProgress', mp.progress, mp.message);
                    break;
                    
                case RuntimeBridgeWorkerMessageType.uploadFailed:
                    const mf = message as UploadFailedMessage;
                    this.signalsService.emit('uploadFailed', mf.message);
                    break;
                    
                case RuntimeBridgeWorkerMessageType.uploadSuccess:
                    const ms = message as UploadSuccessMessage;
                    this.signalsService.emit('uploadSuccess', ms.message);
                    break;
            }
        });
    }

    /**
     * Uploads a response file
     * @param filename - Name of file to upload
     * @param apiUrl - API URL to use for the response upload
     * @param fileData - Optional file data blob to upload directly without retrieving from cache
     * @param deleteTemporaryResponseDataOnSuccess - Optional flag to delete temporary response data store after successful upload
     */
    public uploadResponse(filename: string, apiUrl: string, fileData?: Blob, deleteTemporaryResponseDataOnSuccess?: boolean, recoveryEntryId?: number): void {
        if (!swc.controller) {
            console.error("No service worker controller available");
            return;
        }

        const assessmentUuid = this.getCurrentAssessmentUuid();

        if (fileData) {
            console.log(`Uploading file ${filename} with direct blob data (${fileData.size} bytes)`);
        } else {
            console.log(`Uploading file ${filename}, service worker will retrieve from cache`);
        }

        swc.controller.postMessage(new UploadResponseMessage(filename, apiUrl, assessmentUuid, fileData, deleteTemporaryResponseDataOnSuccess, recoveryEntryId));
    }

    /**
     * Gets the UUID of the currently active assessment, needed by the SW for
     * tagging the upload. Primary source is PWAService.currentMetadata which
     * is persisted to localStorage on assessment load; the TaskInfo path is a
     * legacy fallback (TaskInfo.readables/accessables does not declare
     * assessmentUuid, so its accessor only exists if something sets it
     * dynamically).
     * @returns The current assessment UUID or empty string if not available
     */
    private getCurrentAssessmentUuid(): string {
        const injector = (window as any).angular?.element(document.body).injector();
        if (injector) {
            // Primary: PWAService.getCurrentMetadata().assessment_uuid
            try {
                const pwaService = injector.get('PWAService');
                if (pwaService && typeof pwaService.getCurrentMetadata === 'function') {
                    const metadata = pwaService.getCurrentMetadata();
                    if (metadata && metadata.assessment_uuid) {
                        return metadata.assessment_uuid;
                    }
                }
            } catch (e) {
                // PWAService not registered — fall through to TaskInfo fallback.
            }

            // Fallback: legacy TaskInfo accessor.
            try {
                const taskInfoService = injector.get('TaskInfoService');
                if (taskInfoService && typeof taskInfoService.getTaskInfo === 'function') {
                    const taskInfo = taskInfoService.getTaskInfo();
                    if (taskInfo && typeof taskInfo.getAssessmentUuid === 'function') {
                        return taskInfo.getAssessmentUuid() || '';
                    }
                }
            } catch (e) {
                // Ignore — nothing else to try.
            }
        }
        return '';
    }

    public quit(): void {
        // Stop screen recording if active
        if (this.screenRecordingBridgeClient.isRecording()) {
            console.log('Stopping screen recording before quit');
            this.screenRecordingBridgeClient.stopRecording();
        }

        // Tear down the in-exam session locally before reloading. The /pwa-start
        // $routeChangeSuccess cleanup in app.js only runs when examInProgress is false,
        // so explicit teardown here is what flips that gate. Doing this inline (rather
        // than relying solely on the SW reset path) keeps quit() self-contained and
        // survives a kiosk relaunch racing the QuitMessage round-trip.
        clearSessionResumeStorage();

        // Send quit message to service worker to reset Authentication and AssessmentContentLoader
        if (swc.controller) {
            swc.controller.postMessage(new QuitMessage());
        }

        // Full page reload to start page — destroys all stale controllers cleanly.
        // Uses replace() to avoid leaving the exam route in browser history,
        // then reload() to force a full page reload (hash-only changes don't reload).
        this.$timeout(() => {
            const target = new URL(window.location.href);
            target.hash = '#/pwa-start';
            window.location.replace(target.toString());
            window.location.reload();
        }, 1000);
    }

    public isOnline(): boolean {
        return true;
    }

    public isWindows(): boolean {
        return false;
    }

    public getMediaUrl(): string {
        return '';
    }

    public listKeyboardLanguagesAvailable(): string[] {
        if (!swc.controller) {
            console.error("No service worker controller available");
            return [];
        }

        swc.controller.postMessage(new ListKeyboardLanguagesAvailableMessage());
        return [];
    }

    public setRuntimeLanguage(lang: string): void {
        console.warn("PWARuntimeBridgeClient: setRuntimeLanguage() called with lang:", lang);
        if (!swc.controller) {
            console.error("No service worker controller available");
            return;
        }

        swc.controller.postMessage(new SetRuntimeLanguageMessage(lang));
    }

    public frontendReady(): void {
        // Try to wait for service worker controller to become available
        let attempts = 0;
        const maxAttempts = 25;
        const checkInterval = 500; // 500ms between checks

        const waitForController = () => {
            if (swc.controller) {
                console.log("Service worker controller available - sending frontend ready message");
                swc.controller.postMessage(new FrontendReadyMessage());
                return;
            }

            attempts++;
            if (attempts >= maxAttempts) {
                console.error("No service worker controller available after retries - reloading page");
                this.$timeout(() => {
                    this.notify.error('Initialization error - reloading page');
                });
                this.$timeout(() => {
                    window.location.reload(); 
                }, 1000);
                return;
            }

            this.$timeout(waitForController, checkInterval);
        };

        waitForController();
    }

    public changeKeyboardLanguage(lang: string): void {
        if (!swc.controller) {
            console.error("No service worker controller available");
            return;
        }

        swc.controller.postMessage(new ChangeKeyboardLanguageMessage(lang));
    }
}
